import { useContact, useProperty, useMandate } from "@/OneLiners";
import {
    ActionResponse,
    ActionStatusHelper,
    DOMAIN_CONFIGS,
    DomainFormHook,
    DomainHookProps,
    useAppEnum,
    useFormLifeCycle,
} from "@/Shared";

import { PropertyFormViewModel } from "../ViewModels/PropertyFormViewModel";
import { usePropertyEnricher } from "./usePropertyEnricher";
import { useContactEnricher } from "@/Domains/Contact";
import { useMandateEnricher } from "@/Domains/Mandate";
import { PropertyModel } from "../Models";
import { MatchingComputeService } from "@/Domains/Matching";
import { PropertyValidationSchema } from "../PropertyFormValidation";

/**
 * usePropertyForm hook – manages contact form data and interactions.
 * 
 * Author: Samano CASTRE  
 * Date: July 17, 2025
 * 
 * Responsibilities:
 * - Initialises contact form data by fetching current contact and agent list
 * - Tracks form submission state and response (create or update)
 * - Exposes form logic to the component layer
 * 
 * Behavior:
 * - On mount, fetches the current contact (if editing) and the list of agents
 * - Automatically switches between `createProperty` or `updateProperty` based on contact presence
 * - Tracks the response of the latest action to provide submission feedback
 */
export const usePropertyForm = (props: DomainHookProps): DomainFormHook => {
    const { extraArgs, permissionHook } = props;
    const { contact } = extraArgs ?? {};
    const { domainType } = DOMAIN_CONFIGS.Property;

    /**
     * Contact domain operations and related backend state.
     * Exposes CRUD actions, current entity fetching and associated responses.
     */
    const propertyHook = useProperty();

    /**
    * Users data (used for agent resolution and audit enrichment)
    */
    const { dataMap: contactMap, stateResponse: contactResponse } = useContact();
    const { enricherResponse: contactEnricherResponse, getEnrichedContactMap } = useContactEnricher();

    /**
    * Users data (used for agent resolution and audit enrichment)
    */
    const { dataMap: mandateMap, stateResponse: mandateResponse } = useMandate();
    const { enricherResponse: mandateEnricherResponse, getEnrichedMandateMap } = useMandateEnricher();


    /**
     * Contact enrichment layer.
     * Provides additional data (users, computed fields, etc.)
     * required to build the form view model.
     */
    const {
        enricherResponse,
        getEnrichedProperty,
        sectorMap,
        cityMap
    } = usePropertyEnricher();

    /**
         * Contact-related enums exposed to the UI layer.
         * Used to build the form view model.
         */
    const { PropertyEnums, MandateEnums } = useAppEnum();

    const response: ActionResponse = {
        status: ActionStatusHelper.combine(
            propertyHook.stateResponse.status,
            contactResponse.status,
            mandateResponse.status,
            enricherResponse.status,
            contactEnricherResponse.status,
            permissionHook.stateResponse.status,
            mandateEnricherResponse.status
        ),
        error: propertyHook.stateResponse.error ??
            contactResponse.error ??
            mandateResponse.error ??
            enricherResponse.error ??
            contactEnricherResponse.error ??
            mandateEnricherResponse.error
    };

    const lifeCycleHook = useFormLifeCycle({
        response,
        domainType,
        props,
        entityHook: propertyHook,
        buildVM: (currentEntity: PropertyModel) => {
            return new PropertyFormViewModel({
                viewEnums: PropertyEnums,
                mandateEnums: MandateEnums,
                mandateModelMap: getEnrichedMandateMap(mandateMap),
                contactModelMap: getEnrichedContactMap(contactMap),
                sectorModelMap: sectorMap,
                cityModelMap: cityMap,
                property: getEnrichedProperty(currentEntity),
                contact
            })
        },
        onSuccess: async (entity: PropertyModel) => {
            try { MatchingComputeService.computeMatchingsBy('property', entity.id); }
            catch (error) { }
        },
    });

    /**
     * Public API exposed to the component layer.
     * Only data and actions strictly required by the UI are returned.
     */
    return {
        // Form
        ...lifeCycleHook,
        viewEnums: PropertyEnums,
        ValidationSchema: PropertyValidationSchema(PropertyEnums),
    }
}